import {
  AppWindow,
  BadgeDollarSign,
  Boxes,
  Building2,
  ChartNoAxesCombined,
  CreditCard,
  FileBox,
  Gift,
  HeartHandshake,
  Languages,
  Megaphone,
  MessageSquareText,
  MonitorSmartphone,
  PackageOpen,
  PackageSearch,
  PlugZap,
  ReceiptText,
  Repeat2,
  Rocket,
  ShoppingCart,
  Smartphone,
  Sparkles,
  SlidersHorizontal,
  Store,
  Truck,
  Users,
  WalletCards,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { PermissionString } from '@/lib/constants/permissions';
import { PLATFORM_CARRIERS } from '@/lib/constants/carriers';

export type CapabilityGroup = 'operate' | 'sell' | 'grow' | 'extend';
export type CapabilityPortfolio = 'core' | 'addOn' | 'integration' | 'separateProduct' | 'platform';
export type CapabilityStage = 'connected' | 'conditional' | 'controlled' | 'certification' | 'separate';
export type RouteAccess = 'available' | 'conditional' | 'obligation' | 'blocked' | 'separate' | 'system';

export interface CapabilityRoute {
  path: string;
  match?: 'prefix' | 'exact';
  permission: PermissionString | null;
  access?: RouteAccess;
}

export interface PlatformCapability {
  id: string;
  group: CapabilityGroup;
  portfolio: CapabilityPortfolio;
  stage: CapabilityStage;
  titleKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  /** Safe landing page. Certification-only cards deliberately omit this. */
  href?: string;
  featureRequired?: string;
  backendCapabilityCodes?: readonly string[];
  routes: readonly CapabilityRoute[];
}

const route = (
  path: string,
  permission: PermissionString | null,
  access: RouteAccess = 'available',
  match: CapabilityRoute['match'] = 'prefix',
): CapabilityRoute => ({ path, permission, access, match });

/**
 * Product and route portfolio for the merchant dashboard.
 *
 * This registry describes what the UI may expose. Backend capability gates
 * remain authoritative for mutations, events, jobs, settlement and cleanup.
 * A commercial feature flag can make a conditional surface visible, but it
 * can never turn a certification-stage capability into new activity.
 */
export const PLATFORM_CAPABILITIES: readonly PlatformCapability[] = [
  {
    id: 'catalog', group: 'operate', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.catalog.title',
    descriptionKey: 'capabilityCenter.items.catalog.description',
    icon: PackageOpen, href: '/products',
    routes: [
      route('/products/new', 'CreateCatalog', 'available', 'exact'),
      route('/products', 'ReadCatalog'),
      route('/categories/new', 'CreateCatalog', 'available', 'exact'),
      route('/categories', 'ReadCatalog'),
      route('/collections/new', 'CreateCatalog', 'available', 'exact'),
      route('/collections', 'ReadCatalog'),
      // /facets is a READ-ONLY advanced view of the facets the system projects
      // from Attributes; /facets/new and the /filters aliases now redirect to
      // it, so advertising them as CreateCatalog capabilities would promise a
      // create flow that no longer exists.
      route('/facets', 'ReadCatalog'),
      route('/filters', 'ReadCatalog'),
      route('/attributes', 'ReadCatalog'),
      route('/brands/new', 'CreateCatalog', 'available', 'exact'),
      route('/brands', 'ReadCatalog'),
      route('/catalog/labels/new', 'ManageProductLabel', 'available', 'exact'),
      route('/catalog/labels', 'ReadProductLabel'),
      route('/product-properties', 'ReadCatalog'),
      route('/product-templates', 'ReadCatalog'),
    ],
  },
  {
    id: 'digital-delivery', group: 'sell', portfolio: 'addOn', stage: 'certification',
    titleKey: 'digitalFiles.title',
    descriptionKey: 'capabilities.digitalDeliveryReadOnly',
    icon: FileBox,
    backendCapabilityCodes: ['digitalDelivery'],
    routes: [],
  },
  {
    id: 'inventory', group: 'operate', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.inventory.title',
    descriptionKey: 'capabilityCenter.items.inventory.description',
    icon: PackageSearch, href: '/inventory',
    routes: [
      route('/stock-locations/new', 'UpdateSettings', 'available', 'exact'),
      route('/stock-locations', 'UpdateSettings'),
      route('/inventory', 'ReadCatalog'),
    ],
  },
  {
    id: 'procurement', group: 'operate', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.procurement.title',
    descriptionKey: 'capabilityCenter.items.procurement.description',
    icon: Repeat2,
    routes: [
      route('/inventory/transfers/new', 'CreateInventory', 'conditional', 'exact'),
      route('/inventory/transfers', 'ReadInventory', 'conditional'),
      route('/inventory/purchase-orders/vendors/new', 'CreatePurchaseOrder', 'conditional', 'exact'),
      route('/inventory/purchase-orders/vendors', 'ReadPurchaseOrder', 'conditional'),
      route('/inventory/purchase-orders/new', 'CreatePurchaseOrder', 'conditional', 'exact'),
      route('/inventory/purchase-orders', 'ReadPurchaseOrder', 'conditional'),
      route('/transfers', 'ReadInventory', 'blocked'),
      route('/purchase-orders', 'ReadPurchaseOrder', 'blocked'),
      route('/vendors', 'ReadPurchaseOrder', 'blocked'),
    ],
  },
  {
    id: 'orders', group: 'operate', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.orders.title',
    descriptionKey: 'capabilityCenter.items.orders.description',
    icon: ShoppingCart, href: '/orders',
    routes: [
      route('/orders/new', 'CreateOrder', 'available', 'exact'),
      route('/orders/drafts', 'CreateOrder'),
      route('/orders/draft', 'CreateOrder'),
      route('/orders', 'ReadOrder'),
    ],
  },
  {
    id: 'fulfilment', group: 'operate', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.fulfilment.title',
    descriptionKey: 'capabilityCenter.items.fulfilment.description',
    icon: Truck, href: '/orders/fulfilment',
    routes: [
      route('/orders/returns', 'ReadReturn'),
      route('/orders/invoices', 'ReadInvoice'),
      route('/orders/shipments', 'ReadShipping'),
      route('/orders/fulfilment', 'ReadOrder'),
    ],
  },
  {
    id: 'customers', group: 'operate', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.customers.title',
    descriptionKey: 'capabilityCenter.items.customers.description',
    icon: Users, href: '/customers',
    routes: [
      route('/customers/new', 'CreateCustomer', 'available', 'exact'),
      route('/customers', 'ReadCustomer'),
    ],
  },
  {
    id: 'promotions', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.promotions.title',
    descriptionKey: 'capabilityCenter.items.promotions.description',
    icon: BadgeDollarSign, href: '/marketing/discounts',
    routes: [
      route('/marketing/catalog-discounts/new', 'CreatePromotion', 'conditional', 'exact'),
      route('/marketing/catalog-discounts', 'ReadCatalog', 'conditional'),
      route('/marketing/cashback/new', 'CreatePromotion', 'available', 'exact'),
      route('/marketing/cashback', 'ReadPromotion'),
      route('/marketing/discounts/new', 'CreatePromotion', 'available', 'exact'),
      route('/marketing/calendar', 'ReadPromotion'),
      route('/marketing/discounts', 'ReadPromotion'),
    ],
  },
  {
    id: 'online-store', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.onlineStore.title',
    descriptionKey: 'capabilityCenter.items.onlineStore.description',
    icon: Store, href: '/sales-channels/online-store',
    routes: [
      route('/sales-channels/online-store/product-experience', 'UpdateSettings'),
      route('/sales-channels/online-store/design', 'UpdateSettings'),
      route('/sales-channels/online-store', 'ReadSalesChannel'),
      route('/sales-channels', 'ReadSalesChannel'),
    ],
  },
  {
    id: 'payments', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.payments.title',
    descriptionKey: 'capabilityCenter.items.payments.description',
    icon: CreditCard, href: '/settings/payment-methods',
    routes: [route('/settings/payment-methods', 'ReadSettings')],
  },
  {
    id: 'shipping', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.shipping.title',
    descriptionKey: 'capabilityCenter.items.shipping.description',
    icon: Truck, href: '/settings/shipping',
    routes: [
      route('/settings/shipping/manual', 'ReadShipping'),
      route('/settings/shipping', 'ReadShipping', 'available', 'exact'),
    ],
  },
  {
    id: 'carrier-shipping', group: 'sell', portfolio: 'integration', stage: 'certification',
    titleKey: 'capabilityCenter.items.carrierShipping.title',
    descriptionKey: 'capabilityCenter.items.carrierShipping.description',
    icon: Truck,
    featureRequired: 'carrierShipping',
    backendCapabilityCodes: ['carrierShipping'],
    routes: [
      route('/settings/shipping/kayn-ship', 'ReadShipping', 'blocked'),
      ...PLATFORM_CARRIERS.map(carrier =>
        route(`/settings/shipping/${carrier.code}`, 'ReadShipping', 'blocked'),
      ),
    ],
  },
  {
    id: 'reports', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.reports.title',
    descriptionKey: 'capabilityCenter.items.reports.description',
    icon: ChartNoAxesCombined, href: '/reports',
    routes: [route('/reports', 'ReadAnalytics'), route('/finance', 'ReadAnalytics')],
  },
  {
    id: 'content', group: 'sell', portfolio: 'core', stage: 'connected',
    titleKey: 'capabilityCenter.items.content.title',
    descriptionKey: 'capabilityCenter.items.content.description',
    icon: Megaphone, href: '/marketing/announcements',
    routes: [
      route('/marketing/announcements', 'ReadSettings'),
      route('/blog/new', 'UpdateSettings', 'available', 'exact'),
      route('/blog', 'ReadSettings'),
      route('/pages/new', 'UpdateSettings', 'available', 'exact'),
      route('/pages', 'ReadSettings'),
      route('/settings/content-blocks', 'ReadSettings'),
    ],
  },
  {
    id: 'localization', group: 'sell', portfolio: 'core', stage: 'conditional',
    titleKey: 'capabilityCenter.items.localization.title',
    descriptionKey: 'capabilityCenter.items.localization.description',
    icon: Languages, href: '/settings/markets', featureRequired: 'multiMarket',
    routes: [
      route('/settings/markets', 'UpdateSettings', 'conditional'),
      route('/settings/languages', 'UpdateSettings', 'conditional'),
      route('/settings/domains', 'ReadSettings', 'conditional'),
      route('/marketing/seo', 'ReadSettings', 'conditional'),
    ],
  },
  {
    id: 'messaging', group: 'sell', portfolio: 'integration', stage: 'conditional',
    titleKey: 'capabilityCenter.items.messaging.title',
    descriptionKey: 'capabilityCenter.items.messaging.description',
    icon: MessageSquareText, href: '/settings/messaging',
    routes: [route('/settings/messaging', 'ReadSettings', 'conditional')],
  },
  {
    id: 'engagement', group: 'grow', portfolio: 'addOn', stage: 'controlled',
    titleKey: 'capabilityCenter.items.engagement.title',
    descriptionKey: 'capabilityCenter.items.engagement.description',
    icon: HeartHandshake,
    backendCapabilityCodes: ['reviews', 'productQa', 'wishlist', 'backInStockNotifications', 'testimonials'],
    routes: [
      route('/customers/reviews', 'ReadReview', 'obligation'),
      route('/customers/review-requests', 'ReadReview', 'obligation'),
      route('/customers/questions', 'ReadProductQA', 'obligation'),
      route('/marketing/back-in-stock', 'ReadCatalog', 'obligation'),
      route('/settings/testimonials', 'ReadTestimonial', 'obligation'),
    ],
  },
  {
    id: 'loyalty-wallet', group: 'grow', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.loyaltyWallet.title',
    descriptionKey: 'capabilityCenter.items.loyaltyWallet.description',
    icon: WalletCards,
    backendCapabilityCodes: ['loyaltyEarn', 'cashbackEarn', 'pointsRedeem', 'walletSpend'],
    routes: [
      route('/reports/loyalty', 'ReadLoyalty', 'obligation'),
      route('/marketing/loyalty', 'ReadLoyalty', 'available'),
    ],
  },
  {
    id: 'gift-cards', group: 'grow', portfolio: 'addOn', stage: 'controlled',
    titleKey: 'capabilityCenter.items.giftCards.title',
    descriptionKey: 'capabilityCenter.items.giftCards.description',
    icon: Gift,
    backendCapabilityCodes: ['giftCardIssue', 'giftCardSpend'],
    routes: [
      route('/gift-cards/settings', 'ManageGiftCards', 'blocked', 'exact'),
      route('/gift-cards', 'ReadGiftCards', 'obligation'),
    ],
  },
  {
    id: 'automation', group: 'grow', portfolio: 'addOn', stage: 'controlled',
    titleKey: 'capabilityCenter.items.automation.title',
    descriptionKey: 'capabilityCenter.items.automation.description',
    icon: Workflow,
    backendCapabilityCodes: ['automationExecution', 'abandonedCartRecovery'],
    routes: [
      route('/orders/automations/new', 'ManageAutomation', 'blocked', 'exact'),
      route('/products/automations/new', 'ManageAutomation', 'blocked', 'exact'),
      route('/customers/automations/new', 'ManageAutomation', 'blocked', 'exact'),
      route('/marketing/automations/new', 'ManageAutomation', 'blocked', 'exact'),
      route('/orders/automations', 'ReadAutomation', 'obligation'),
      route('/products/automations', 'ReadAutomation', 'obligation'),
      route('/customers/automations', 'ReadAutomation', 'obligation'),
      route('/marketing/automations', 'ReadAutomation', 'obligation'),
      route('/marketing/abandoned-carts', 'ReadOrder', 'obligation'),
    ],
  },
  {
    id: 'partner-growth', group: 'grow', portfolio: 'addOn', stage: 'controlled',
    titleKey: 'capabilityCenter.items.partnerGrowth.title',
    descriptionKey: 'capabilityCenter.items.partnerGrowth.description',
    icon: Sparkles,
    backendCapabilityCodes: ['affiliateAccrual', 'marketingAttribution'],
    routes: [
      route('/marketing/affiliates/new', 'ManageAffiliate', 'blocked', 'exact'),
      route('/marketing/affiliates', 'ReadAffiliate', 'obligation'),
      route('/marketing/referrals', 'ReadLoyalty', 'blocked'),
      route('/marketing/repurchase-reminders', 'ReadPromotion', 'blocked'),
      route('/marketing/sales-boosters', 'ReadPromotion', 'blocked'),
      route('/marketing/push-notifications', 'ReadSettings', 'blocked'),
      route('/marketing/segments', 'ReadSmartSegment', 'blocked'),
      route('/customers/rfm', 'ReadSmartSegment', 'blocked'),
      route('/customers/smart-segments', 'ReadSmartSegment', 'blocked'),
      route('/marketing', 'ReadPromotion', 'blocked'),
    ],
  },
  {
    id: 'product-feeds', group: 'grow', portfolio: 'integration', stage: 'certification',
    titleKey: 'capabilityCenter.items.productFeeds.title',
    descriptionKey: 'capabilityCenter.items.productFeeds.description',
    icon: Rocket,
    routes: [route('/marketing/product-feeds', 'ReadSettings', 'blocked')],
  },
  {
    id: 'apps', group: 'extend', portfolio: 'integration', stage: 'controlled',
    titleKey: 'capabilityCenter.items.apps.title',
    descriptionKey: 'capabilityCenter.items.apps.description',
    icon: PlugZap,
    backendCapabilityCodes: ['appCenter'],
    routes: [
      route('/settings/apps', 'ReadSettings', 'obligation'),
      route('/settings/integrations', 'ReadWebhook', 'blocked'),
    ],
  },
  {
    id: 'data-migration', group: 'extend', portfolio: 'platform', stage: 'controlled',
    titleKey: 'capabilityCenter.items.dataMigration.title',
    descriptionKey: 'capabilityCenter.items.dataMigration.description',
    icon: FileBox,
    backendCapabilityCodes: ['dataMigration'],
    routes: [route('/settings/data-migration', 'ReadSettings', 'obligation')],
  },
  {
    id: 'advanced-theme', group: 'extend', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.advancedTheme.title',
    descriptionKey: 'capabilityCenter.items.advancedTheme.description',
    icon: MonitorSmartphone,
    backendCapabilityCodes: ['advancedThemeWidgets'],
    routes: [],
  },
  {
    id: 'fixed-bundles', group: 'extend', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.fixedBundles.title',
    descriptionKey: 'capabilityCenter.items.fixedBundles.description',
    icon: Boxes,
    backendCapabilityCodes: ['fixedBundles'],
    routes: [],
  },
  {
    id: 'customizations', group: 'extend', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.customizations.title',
    descriptionKey: 'capabilityCenter.items.customizations.description',
    icon: SlidersHorizontal,
    backendCapabilityCodes: ['productCustomizations', 'orderCustomizations'],
    routes: [route('/settings/order-customizations', 'ReadSettings', 'obligation')],
  },
  {
    id: 'giftExperiences', group: 'extend', portfolio: 'addOn', stage: 'certification',
    titleKey: 'capabilityCenter.items.giftExperiences.title',
    descriptionKey: 'capabilityCenter.items.giftExperiences.description',
    icon: Gift,
    backendCapabilityCodes: ['giftWrap', 'giftOrders'],
    routes: [route('/settings/gift-options', 'ReadSettings', 'obligation')],
  },
  {
    id: 'pos', group: 'extend', portfolio: 'separateProduct', stage: 'separate',
    titleKey: 'capabilityCenter.items.pos.title',
    descriptionKey: 'capabilityCenter.items.pos.description',
    icon: ReceiptText,
    backendCapabilityCodes: ['posProduct'],
    routes: [route('/sales-channels/pos', 'ReadSalesChannel', 'separate')],
  },
  {
    id: 'merchant-mobile', group: 'extend', portfolio: 'separateProduct', stage: 'separate',
    titleKey: 'capabilityCenter.items.merchantMobile.title',
    descriptionKey: 'capabilityCenter.items.merchantMobile.description',
    icon: Smartphone,
    backendCapabilityCodes: ['merchantMobileApp'],
    routes: [],
  },
  {
    id: 'shopper-mobile', group: 'extend', portfolio: 'separateProduct', stage: 'separate',
    titleKey: 'capabilityCenter.items.shopperMobile.title',
    descriptionKey: 'capabilityCenter.items.shopperMobile.description',
    icon: MonitorSmartphone,
    backendCapabilityCodes: ['shopperMobileApp'],
    routes: [route('/sales-channels/mobile-app', 'ReadSalesChannel', 'separate')],
  },
  {
    id: 'qcommerce', group: 'extend', portfolio: 'separateProduct', stage: 'separate',
    titleKey: 'capabilityCenter.items.qcommerce.title',
    descriptionKey: 'capabilityCenter.items.qcommerce.description',
    icon: Rocket,
    backendCapabilityCodes: ['qcommerce'],
    routes: [],
  },
  {
    id: 'wholesale', group: 'extend', portfolio: 'separateProduct', stage: 'separate',
    titleKey: 'capabilityCenter.items.wholesale.title',
    descriptionKey: 'capabilityCenter.items.wholesale.description',
    icon: Building2,
    backendCapabilityCodes: ['wholesaleB2b'],
    routes: [],
  },
  {
    id: 'platform-admin', group: 'extend', portfolio: 'platform', stage: 'connected',
    titleKey: 'capabilityCenter.items.platformAdmin.title',
    descriptionKey: 'capabilityCenter.items.platformAdmin.description',
    icon: AppWindow, href: '/settings',
    routes: [
      route('/settings/profile', null),
      route('/settings/metafield-definitions', 'ManageMetafieldDefinitions'),
      route('/settings/staff/invite', 'CreateAdministrator'),
      route('/settings/staff', 'ReadAdministrator'),
      route('/settings/return-policy', 'ManageReturnSettings'),
      route('/settings/scheduling', 'ReadScheduling'),
      route('/settings/invoices', 'ReadInvoice'),
      route('/settings/checkout-restrictions', 'UpdateSettings'),
      route('/settings/inventory', 'UpdateSettings'),
      route('/settings/operations', 'UpdateSettings'),
      route('/settings', 'ReadSettings'),
    ],
  },
] as const;

const SYSTEM_ROUTES: readonly CapabilityRoute[] = [
  route('/', null, 'system', 'exact'),
  route('/capabilities', null, 'system'),
  route('/notifications', null, 'system'),
  route('/onboarding', null, 'system'),
  route('/select-store', null, 'system'),
  route('/login', null, 'system'),
  route('/signup', null, 'system'),
  route('/accept-invite', null, 'system'),
  route('/reset-password', null, 'system'),
  route('/verify-email', null, 'system'),
  route('/verify-2fa', null, 'system'),
];

const ALL_ROUTES = [
  ...PLATFORM_CAPABILITIES.flatMap(capability =>
    capability.routes.map(surface => ({ ...surface, capability })),
  ),
  ...SYSTEM_ROUTES.map(surface => ({ ...surface, capability: undefined })),
].sort((a, b) => b.path.length - a.path.length || (a.match === 'exact' ? -1 : 1));

function routeMatches(pathname: string, surface: CapabilityRoute): boolean {
  if (surface.match === 'exact') return pathname === surface.path;
  return pathname === surface.path || pathname.startsWith(`${surface.path}/`);
}

export function resolveDashboardRoute(pathname: string) {
  return ALL_ROUTES.find(surface => routeMatches(pathname, surface));
}

export function getRoutePermission(pathname: string): PermissionString | null {
  return resolveDashboardRoute(pathname)?.permission ?? null;
}

export function isBlockedCapabilityRoute(pathname: string): boolean {
  const access = resolveDashboardRoute(pathname)?.access;
  return access === 'blocked' || access === 'separate';
}

/**
 * Dashboard presentation gate for backend capabilities which are not yet
 * certified for new merchant activity. Unknown codes fail closed. Backend
 * service gates remain authoritative for every write and lifecycle action.
 */
export function isBackendCapabilityNewActivityBlocked(code: string): boolean {
  const capability = PLATFORM_CAPABILITIES.find(item =>
    item.backendCapabilityCodes?.includes(code),
  );
  if (!capability) return true;
  return capability.stage === 'controlled'
    || capability.stage === 'certification'
    || capability.stage === 'separate';
}

export function getCapabilityById(id: string): PlatformCapability | undefined {
  return PLATFORM_CAPABILITIES.find(capability => capability.id === id);
}

export const capabilityGroups: readonly CapabilityGroup[] = ['operate', 'sell', 'grow', 'extend'];
